import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import InfoRowItemWithActions from '@europrocurement/flexy-components/components/organisms/InfoRowItemWithActions/InfoRowItemWithActions';
import {
    selectedPrescripteursSelector,
    SousClient,
    SousClientApi,
    SousClientsSelector,
    useSociete,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import { faBuildingUser } from '@fortawesome/pro-regular-svg-icons';
import { ContinueButton, useModal } from '@europrocurement/flexy-components';
import SelectSociete from './SelectSociete';

export type SelectSocieteWrapperProps = {
    onSelect: (
        ids: (number | undefined)[],
        sousClients: SousClientApi[],
        sousClientsOpere: SousClientApi[],
    ) => void;
    title?: string;
    submitButtonLabel?: string;
    displayOpere?: boolean;
    defaultOpere?: number;
    defaultFacturee?: number;
    idFormalites: Array<number>;
};

const SelectSocieteWrapper: React.FunctionComponent<SelectSocieteWrapperProps> = function ({
    onSelect,
    title = 'Pour quelle société est destinée la formalité ?',
    submitButtonLabel = 'Continuer',
    displayOpere = true,
    defaultOpere,
    defaultFacturee,
    idFormalites,
}) {
    const datasource = useTiersServiceSelector(SousClientsSelector).main;
    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);
    const societeId = useSociete();
    const { palette } = useTheme();
    const { modalActions } = useModal();
    const [operatedClient, setOperatedClient] = useState<SousClientApi | undefined>();
    const [billedClient, setBilledClient] = useState<SousClientApi | undefined>();
    const [, setSousClients] = useState<SousClient[]>([]);

    useEffect(() => {
        // dafc[2] -> Facture | 1 = Prescripteur -  2 = Client
        if (
            prescripteur &&
            !societeId &&
            prescripteur?.relationsSocietes[0].dafc &&
            prescripteur.relationsSocietes[0].dafc[2] === '1'
        ) {
            // cas société par défaut
            setBilledClient(prescripteur);
        } else if (prescripteur && societeId && prescripteur.relationsSocietes) {
            // cas d'une autre société selectionnée
            const relatedCompany = prescripteur.relationsSocietes.find(
                (relation) => relation.societe?.id === societeId,
            );
            if (relatedCompany?.dafc && relatedCompany.dafc[2] === '1') {
                setBilledClient(prescripteur);
            }
        }
    }, [prescripteur, societeId]);

    // get tiers/client related to the collection
    useEffect(() => {
        if (datasource.status === 'succeeded') {
            setSousClients(datasource.data);
            if (defaultOpere) {
                const opere = datasource.data.find((sousClient) => defaultOpere === sousClient.id);
                setOperatedClient(opere);
            }
            if (defaultFacturee) {
                const facture = datasource.data.find(
                    (sousClient) => Number(defaultFacturee) === sousClient.id,
                );
                setBilledClient(facture);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasource.status]);

    const handleUpdateSociete = (
        type: 'OPERATED' | 'BILLED',
        onSelectSociete: React.Dispatch<React.SetStateAction<SousClientApi | undefined>>,
    ) => {
        if (prescripteur) {
            let titleModal = 'Pour quelle(s) société(s) est destinée la commande ?';
            if (type === 'OPERATED') {
                titleModal = 'Pour quelle société opérée est destinée la commande ?';
            } else if (type === 'BILLED') {
                titleModal = 'Pour quelle société facturée est destinée la commande ?';
            }

            const selectSociety = (society: SousClientApi | undefined) => {
                onSelectSociete(society);
                // On change on the operated society,  update the billed client directly if undefined yet
                if (type === 'OPERATED' && !billedClient) {
                    setBilledClient(society);
                }
            };

            modalActions.call(
                <SelectSociete
                    title={titleModal}
                    description=""
                    multiple={false}
                    onSelect={(ids, sousClientSelect) => {
                        if (sousClientSelect.length === 1) {
                            selectSociety(sousClientSelect[0]);
                            if (!displayOpere && type === 'BILLED') {
                                onSelect([sousClientSelect[0]]);
                            }
                        }
                        modalActions.close();
                    }}
                    idFormalites={idFormalites}
                />,
            );
        }
    };

    const handleSubmit = useCallback(() => {
        if (onSelect && billedClient && operatedClient) {
            onSelect([billedClient.id], [billedClient], [operatedClient]);
        }
    }, [onSelect, billedClient, operatedClient]);

    return (
        <Box>
            <Box
                sx={{
                    width: '100%',
                }}
            >
                {title !== '' && (
                    <Typography
                        className="title"
                        variant="l2dh4"
                        sx={{
                            color: palette.primary.text,
                        }}
                    >
                        {title}
                    </Typography>
                )}
                {displayOpere && (
                    <Box>
                        <Typography variant="l2dsubtitles">Société opérée</Typography>
                        <InfoRowItemWithActions
                            info={
                                operatedClient ? (
                                    <>
                                        <Typography variant="l2dsubtitles">
                                            {operatedClient.raisonSociale}
                                        </Typography>
                                        <Typography variant="l2dcaption">
                                            {operatedClient.siren}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography
                                        variant="l2dsubtitles"
                                        sx={{ color: palette.warning.main }}
                                    >
                                        Sociétée non définie
                                    </Typography>
                                )
                            }
                            icon={faBuildingUser}
                            rowSx={{ padding: '25px 28px 25px 35px' }}
                            singleActionOnClick={() =>
                                handleUpdateSociete('OPERATED', setOperatedClient)
                            }
                        />
                    </Box>
                )}

                {/* billedClient society info and edit action */}
                <Box>
                    <Typography variant="l2dsubtitles">Société facturée</Typography>
                    <InfoRowItemWithActions
                        info={
                            billedClient ? (
                                <>
                                    <Typography variant="l2dsubtitles">
                                        {billedClient.raisonSociale}
                                    </Typography>
                                    <Typography variant="l2dcaption">
                                        {billedClient.siren}
                                    </Typography>
                                </>
                            ) : (
                                <Typography
                                    variant="l2dsubtitles"
                                    sx={{ color: palette.warning.main }}
                                >
                                    Sociétée non définie
                                </Typography>
                            )
                        }
                        rowSx={{ padding: '25px 28px 25px 35px' }}
                        icon={faBuildingUser}
                        singleActionOnClick={() => handleUpdateSociete('BILLED', setBilledClient)}
                    />
                </Box>
            </Box>
            {displayOpere && (
                <Box
                    className="actions"
                    sx={{
                        width: '100%',
                        marginTop: '30px',
                    }}
                >
                    <ContinueButton
                        onClick={handleSubmit}
                        disabled={!billedClient || !operatedClient}
                        libelle={submitButtonLabel}
                    />
                </Box>
            )}
        </Box>
    );
};

export default SelectSocieteWrapper;
